/**
 * <gg-promotions-pdp> — PDP promotions surface (Priority 0 in "Deine Routine").
 *
 * Separate from the cart widget by design: this is single-product-driven and
 * read-only for progress (it never mutates the cart the way the drawer does — a
 * gift is claimed in the cart). It shows promotions that apply to THIS product,
 * with live progress read from /cart.js. Promo rows are ADDITIVE — they sit above
 * the existing bundle/recs (which render as they do today); no coordination between
 * the two async timelines. No match → renders nothing and the existing bundle/recs
 * cascade is the fallback.
 */

import { ThemeEvents } from '@theme/events';
import {
  fetchActivePromotions,
  fetchCart,
  matchingPromotions,
  showsOn,
  progressToward,
  mediaHTML,
  giftDetail,
  localeText,
  isConsumed,
  showsBar,
  titleFor,
  barHTML,
  giftActionHTML,
  animateProgressFills,
  esc,
  requestClaimInCart,
} from '@theme/promotions-shared';

class GgPromotionsPdp extends HTMLElement {
  #lang = 'de';
  #max = 2;
  #subject = null;
  #recsObserver = null;
  #recsTimer = 0;

  connectedCallback() {
    this.#lang = this.dataset.lang || 'de';
    this.#max = parseInt(this.dataset.max) || 2;
    if (this.dataset.giftIcon) window.__ggGiftIcon = this.dataset.giftIcon;
    this.#subject = {
      vendor: this.dataset.vendor || '',
      productId: this.dataset.productId || '',
      variantIds: (this.dataset.variantIds || '').split(',').filter(Boolean),
    };
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#teardownRecsWatch();
  }

  #onCartUpdate = () => {
    this.#render();
  };

  async #render() {
    const [promotions, cart] = await Promise.all([
      fetchActivePromotions(),
      fetchCart(),
    ]);

    const matched = matchingPromotions(promotions, this.#subject)
      .filter((p) => showsOn(p, 'pdp')) // honor Flow surface toggles
      .filter((p) => !isConsumed(cart, p)) // claimed gift OR maxed-out tier → drop it here too
      .slice(0, this.#max);
    this.innerHTML = (await Promise.all(matched.map((p) => this.#renderPromo(p, cart)))).join('');
    if (matched.length) { animateProgressFills(this); this.#wire(); }
    // Promo is higher-priority than related products: it takes the top slot(s), and the
    // recs backfill only the remainder so the block totals `max` rows, not more.
    this.#capRecs(this.#max - matched.length);
  }

  async #renderPromo(promo, cart) {
    const progress = progressToward(cart, promo);
    const text = localeText(promo, this.#lang) || {};
    const [media, detail] = await Promise.all([mediaHTML(promo, cart), giftDetail(promo)]);
    const detailLine = detail ? `<div class="promo-detail">${esc(detail)}</div>` : '';
    // Met gift → claim/choose control, handed off to the cart widget (PDP never mutates).
    const action = giftActionHTML(promo, progress);
    const bar = showsBar(promo, progress) ? barHTML(promo, progress, text, cart) : '';
    return `
      <div class="gg-promo__row" data-promo-id="${promo.id}">
        <div class="promo-media">${media}</div>
        <div class="gg-promo__body">
          <div class="promo-title"><span class="promo-dot"></span>${titleFor(promo, text)}</div>
          ${detailLine}
          ${action}
          ${bar}
        </div>
      </div>`;
  }

  #wire() {
    // stopPropagation so the click doesn't bubble to the cart drawer's outside-click
    // dismiss — requestClaimInCart opens the drawer, and without this the same click
    // immediately closes it again (drawer "flashes" and the claim/picker never lands).
    this.querySelectorAll('[data-promo-claim]').forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        requestClaimInCart(btn.dataset.promoClaim, 'claim');
      })
    );
    this.querySelectorAll('[data-promo-pick]').forEach((btn) =>
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        requestClaimInCart(btn.dataset.promoPick, 'pick');
      })
    );
  }

  /**
   * Cap the sibling recs to `remaining` slots. The recs render async on their own
   * timeline, so we both set their limit (if they haven't rendered) AND trim any
   * already-rendered cards, and keep watching briefly in case they arrive after us.
   */
  #capRecs(remaining) {
    const block = this.closest('.gg-quickadd');
    const recs = block?.querySelector('gg-quickadd-recs');
    if (!recs) return;
    const cap = Math.max(0, remaining);

    // HIDE overflow cards, never remove them — so when a promo is later claimed and
    // `remaining` grows (fewer promos, more rec slots), the hidden cards come back
    // without a reload. Use inline display (NOT the [hidden] attr: .gg-quickadd__card sets
    // `display:flex`, which overrides [hidden]'s UA `display:none` → the card stays visible).
    const trim = () => {
      recs.dataset.limit = String(cap);
      recs.querySelectorAll('.gg-quickadd__card').forEach((c, i) => {
        c.style.display = i >= cap ? 'none' : '';
      });
      recs.style.display = cap <= 0 ? 'none' : '';
    };
    trim();

    // Recs may populate after us. Watch briefly and re-apply — but tear down any prior
    // watch first so rapid re-renders don't stack observers on the same node.
    this.#teardownRecsWatch();
    if (recs.children.length) return; // already populated → nothing to wait for
    this.#recsObserver = new MutationObserver(trim);
    this.#recsObserver.observe(recs, { childList: true });
    this.#recsTimer = setTimeout(() => this.#teardownRecsWatch(), 4000);
  }

  #teardownRecsWatch() {
    this.#recsObserver?.disconnect();
    this.#recsObserver = null;
    clearTimeout(this.#recsTimer);
  }
}

if (!customElements.get('gg-promotions-pdp')) {
  customElements.define('gg-promotions-pdp', GgPromotionsPdp);
}
